<template>
  <div class="tst-body">
    <div class="tst-cost text-muted">
      <span>任务用时：</span><span>{{ costTime }}</span>
      <a
        class="btn btn-link pull-right tst-sort"
        href="javascript:;"
        :title="sortAsc ? '顺序，点击转为倒序' : '倒序，点击转为顺序'"
        @click.prevent="sortAsc = !sortAsc"
      >
        排序 <i class="fa" :class="sortAsc ? 'fa-sort-numeric-asc' : 'fa-sort-numeric-desc'" />
      </a>
    </div>

    <div class="md-timeline">
      <div v-for="(step, idx) in displaySteps" :key="idx" class="md-timeline-block">
        <div
          class="md-timeline-img"
          :class="step.state === 0 ? 'cd-movie' : 'cd-picture'"
        >
          <i
            class="fa fa-2x"
            :class="step.state === 0 ? 'fa-pencil-square-o' : 'fa-check-square-o'"
            aria-hidden="true"
          />
        </div>

        <div class="md-timeline-content">
          <!-- 第1行：节点名 | 表单 -->
          <div class="tst-row1">
            <div class="pull-left rowName">
              <span class="tst-node">{{ step.nodeName }}</span>
            </div>
            <div class="pull-right">
              <a
                v-if="step.hasForm"
                href="javascript:;"
                @click.prevent="$emit('open-form', step)"
              >表单</a>
            </div>
          </div>

          <!-- 第2行：状态+处理人 | 日期（对齐 Ivan TaskStep.html） -->
          <div class="text-muted small tst-row2 clearfix">
            <div class="tst-row2-left">
              <template v-if="step.state === 0">
                <span class="tst-state text-danger">进行中</span>
                <span v-if="step.handler" class="tst-handler">{{ step.handler }}</span>
              </template>
              <template v-else>
                <span class="tst-state text-success">完成</span>
                <span v-if="step.handler" class="tst-handler">{{ step.handler }}</span>
              </template>
            </div>
            <span class="tst-date" :title="'工作起止时间'">{{ step.dateLabel }}</span>
          </div>

          <!-- IsPick → TaskStep_PickList -->
          <div
            v-if="step.isPick && step.pickList && step.pickList.length"
            class="tst-pick container-fluid text-muted small"
          >
            <div
              v-for="(p, pi) in visiblePicks(step)"
              :key="pi"
              class="tst-pick-item"
            >
              <div class="tst-pick-line clearfix">
                <div class="pull-left">
                  <span class="tst-pick-name">{{ p.caseName }}</span>
                </div>
                <div class="pull-right">
                  <i class="fa fa-angle-double-down" />
                </div>
              </div>
            </div>

            <div
              v-if="step.pickList.length > defaultShowSize"
              class="tst-pick-more"
              @click="toggleUnfold(step)"
            >
              <div v-if="!step.unfolded" class="tst-pick-fade">
                <div class="text-muted">更多<i class="fa fa-angle-double-down" /></div>
              </div>
              <div v-else class="text-muted" style="text-align:center;padding:8px 0;">
                收起<i class="fa fa-angle-double-up" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/** Ivan 常见写法：首因子 + 等 */
function shortItems(items) {
  const parts = String(items || '')
    .split(/[;；、,，]/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (!parts.length) return '等'
  if (parts.length === 1) return `${parts[0]}等`
  return `${parts[0]}等`
}

function isPendingPick(row) {
  return row.status === '待安排采样' || !row.pickDateFrom
}

function isDonePick(row) {
  return row.status === '已完成'
}

function isDoingPick(row) {
  return row.status === '采样中' || row.status === '已提交'
}

function buildPickCaseName(row, project) {
  const place = String(row.monitorPlace || row.inspectSubType || '').trim() || '监测点'
  const items = shortItems(row.inspectItems)
  const projectName = String(
    (project && (project.ProjectName || project.xmmc)) || row.xmmc || row.projectName || ''
  ).trim()
  const typ = String(
    (project && project.ProjectType) || row.projectType || '委托检测'
  ).trim()
  const tail = projectName ? `${projectName}(${typ})` : String(row.caseName || typ)
  return `${place}(${items}):${tail}`
}

function formatMd(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return ''
  return `${d.getMonth() + 1}月${d.getDate()}日`
}

function daysBetween(a, b) {
  return Math.max(0, Math.floor((b.getTime() - a.getTime()) / (24 * 3600 * 1000)))
}

function projectCreatedAt(project) {
  const raw = project && (project.CreateDate || project.ProjectDate || project.createdAt)
  if (!raw) return new Date()
  const d = new Date(String(raw).replace(/-/g, '/'))
  return Number.isNaN(d.getTime()) ? new Date() : d
}

function projectCreator(project) {
  const xm = (project && project.rawXm) || {}
  return (
    project.creator ||
    xm.cjr_mc ||
    xm.cjr ||
    xm.cjyh ||
    xm.wtr ||
    xm.lxr ||
    ''
  )
}

function collectHandlers(list) {
  const handlers = []
  list.forEach((p) => {
    String(p.pickCharge || p.pickStaff || p.pickMembers || '')
      .split(/[;；,，]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((n) => {
        if (!handlers.includes(n)) handlers.push(n)
      })
  })
  return handlers
}

/**
 * Ivan TaskStep：委托下单 → 采样任务管理 → 现场采样(IsPick)
 * 有待安排时管理/现场为进行中，避免「父完成子待安排」
 */
function buildSteps(project, picks) {
  const list = Array.isArray(picks) ? picks : []
  const created = projectCreatedAt(project)
  const dateLabel = formatMd(created) || '—'
  const creator = projectCreator(project)

  const pending = list.filter(isPendingPick)
  const doing = list.filter(isDoingPick)
  const done = list.filter(isDonePick)
  const allArranged = list.length > 0 && pending.length === 0
  const allDone = list.length > 0 && done.length === list.length

  const pickList = list.map((p) => ({
    caseName: buildPickCaseName(p, project),
    status: p.status,
    raw: p
  }))

  const handlers = collectHandlers(list)
  // Ivan 卡片上人名偏少（常 1 人）；优先负责人
  const pickHandler = handlers[0] || creator

  let manageState = 1
  let pickState = 0
  if (!list.length || pending.length > 0) {
    manageState = 0
    pickState = 0
  } else if (allDone) {
    manageState = 1
    pickState = 1
  } else if (allArranged && (doing.length > 0 || done.length > 0)) {
    manageState = 1
    pickState = 0
  }

  return [
    {
      nodeName: '委托下单',
      state: 1,
      handler: creator,
      hasForm: true,
      dateLabel,
      isPick: false,
      pickList: []
    },
    {
      nodeName: '采样任务管理',
      state: manageState,
      handler: pickHandler,
      hasForm: true,
      dateLabel,
      isPick: false,
      pickList: []
    },
    {
      nodeName: '现场采样',
      state: pickState,
      handler: pickHandler,
      hasForm: false,
      dateLabel,
      isPick: true,
      pickList
    }
  ]
}

function buildCostTime(project, steps) {
  const first = projectCreatedAt(project)
  if (!first || Number.isNaN(first.getTime())) return '—'
  const startLabel = formatMd(first)
  const allDone = steps.length && steps.every((s) => s.state !== 0)
  if (allDone) {
    const end = new Date()
    return `${startLabel}至${formatMd(end)}，${daysBetween(first, end)}天`
  }
  return `${startLabel} 至今，${daysBetween(first, new Date())}天`
}

export default {
  name: 'TaskStepTimeline',
  props: {
    project: { type: Object, required: true },
    picks: { type: Array, default: () => [] }
  },
  data() {
    return {
      sortAsc: true,
      defaultShowSize: 1,
      steps: []
    }
  },
  computed: {
    costTime() {
      return buildCostTime(this.project, this.steps)
    },
    displaySteps() {
      const list = this.steps.slice()
      return this.sortAsc ? list : list.reverse()
    }
  },
  watch: {
    project: { immediate: true, handler: 'rebuild' },
    picks: { deep: true, handler: 'rebuild' }
  },
  methods: {
    rebuild() {
      this.steps = buildSteps(this.project, this.picks).map((s) => ({
        ...s,
        unfolded: false
      }))
    },
    visiblePicks(step) {
      if (!step.pickList) return []
      if (step.unfolded || step.pickList.length <= this.defaultShowSize) {
        return step.pickList
      }
      return step.pickList.slice(0, this.defaultShowSize)
    },
    toggleUnfold(step) {
      step.unfolded = !step.unfolded
    }
  }
}
</script>

<style scoped>
/* 对齐 Ivan 时间线视觉；用文档流高度，便于下方挂「本项目表单」 */
.tst-body {
  position: relative;
  overflow: visible;
  background-color: #e9f0f5;
  padding: 10px 10px 0;
}
.tst-cost {
  margin-left: 25px;
  margin-bottom: 4px;
  font-size: 13px;
  min-height: 28px;
  line-height: 28px;
}
.tst-sort {
  margin-right: 6px;
  padding-top: 0;
  font-size: 13px;
  color: #337ab7;
  text-decoration: none;
}
.tst-sort:hover,
.tst-sort:focus {
  color: #23527c;
  text-decoration: none;
}

.md-timeline {
  position: relative;
  padding: 2em 0;
  margin-top: 0.5em;
  margin-bottom: 2em;
  background-color: transparent;
}
.md-timeline::before {
  content: '';
  position: absolute;
  top: 0;
  left: 18px;
  height: 100%;
  width: 4px;
  background: #d7e4ed;
}
.md-timeline-block {
  position: relative;
  margin: 2em 0;
}
.md-timeline-block:after {
  content: '';
  display: table;
  clear: both;
}
.md-timeline-block:first-child {
  margin-top: 0;
}
.md-timeline-block:last-child {
  margin-bottom: 0;
}
.md-timeline-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #fff;
  box-shadow:
    0 0 0 4px white,
    inset 0 2px 0 rgba(0, 0, 0, 0.08),
    0 3px 0 4px rgba(0, 0, 0, 0.05);
}
.md-timeline-img i {
  display: block;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  position: relative;
  left: 50%;
  top: 50%;
  margin-left: -12px;
  margin-top: -12px;
  font-size: 18px;
}
.md-timeline-img.cd-picture {
  background: #75ce66;
}
.md-timeline-img.cd-movie {
  background: #c03b44;
}
.md-timeline-content {
  position: relative;
  margin-left: 60px;
  background: white;
  border-radius: 0.25em;
  padding: 1em;
  box-shadow: 0 3px 0 #d7e4ed;
}
.md-timeline-content::before {
  content: '';
  position: absolute;
  top: 16px;
  right: 100%;
  height: 0;
  width: 0;
  border: 7px solid transparent;
  border-right: 7px solid white;
}

.tst-row1 {
  height: 25px;
  width: 100%;
  overflow: hidden;
}
.tst-node {
  font-size: 15px;
  color: #111;
}
.tst-row2 {
  margin-top: 2px;
}
.tst-row2-left {
  width: calc(100% - 150px);
  float: left;
}
.tst-state {
  display: inline-block;
  width: 37px;
}
.tst-handler {
  margin: 0 5px;
  color: #666;
}
.tst-date {
  float: right;
  color: #999;
}

.tst-pick {
  border: 1px solid rgb(221, 221, 221);
  padding: 8px 15px;
  margin-top: 10px;
  position: relative;
}
.tst-pick-item {
  padding: 8px 0 8px 10px;
  border-bottom: 1px solid #ddd;
  background: #fff;
}
.tst-pick-item:last-of-type {
  border-bottom: none;
}
.tst-pick-line {
  height: 20px;
  width: 100%;
}
.tst-pick-name {
  font-size: 14px;
  color: #111;
}
.tst-pick-more {
  cursor: pointer;
  position: relative;
}
.tst-pick-fade {
  position: relative;
  height: 50px;
  padding-top: 20px;
  text-align: center;
  background: linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
}

.pull-left {
  float: left;
}
.pull-right {
  float: right;
}
.clearfix:after {
  content: '';
  display: table;
  clear: both;
}
.text-success {
  color: #3c763d;
}
.text-danger {
  color: #a94442;
}
.text-muted {
  color: #777;
}
.small {
  font-size: 85%;
}
</style>
